import React, { useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  alpha,
  FormControl,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Select,
  Checkbox,
  Grid,
  Chip,
  Divider,
} from '@mui/material';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import jwtAxios from '@projectK/services/auth/JWT';
import { Fonts } from '@projectK/constants/AppEnums';
import AppInfoView from '@projectK/components/AppInfoView';
import AppLoader from '@projectK/components/AppLoader';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PropTypes from 'prop-types';
import { clearProgress, getSaveProgressUserId } from './handleState';
import CircularProgress from '@mui/material/CircularProgress';

const getColor = (companyTheme) => {
  return companyTheme?.primary?.main || '#2196f3';
};
const getbColor = (companyTheme) => {
  return companyTheme
    ? alpha(companyTheme?.primary?.main, 0.8)
    : alpha('#2196f3', 0.8);
};

const canFormSubmit = (
  loader,
  isAddressComplete,
  selectedAddress,
  paymentOption,
  selectedSavedCardId,
  isFormValid,
) => {
  return (
    !loader &&
    isAddressComplete(selectedAddress) &&
    (paymentOption == 'saved' ? !!selectedSavedCardId : isFormValid)
  );
};
const brandLabel = (b) =>
  b ? String(b).charAt(0).toUpperCase() + String(b).slice(1) : 'Card';
const normalizeCardBrandKey = (brand) => {
  const value = String(brand || '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  if (value.includes('visa')) return 'visa';
  if (value.includes('mastercard') || value.includes('master')) {
    return 'mastercard';
  }
  if (value.includes('amex') || value.includes('americanexpress')) {
    return 'amex';
  }
  if (value.includes('discover') || value.includes('disc')) {
    return 'discover';
  }
  return '';
};
const getTotalPrice = (cartItems) => {
  let total = 0;
  cartItems?.map((data) => {
    let price;

    // Check if payment_plan_id exists and is_deposit is true
    if (data.payment_plan_id && data.is_deposit) {
      price = data.remaining_amount * data.payment_plan_id;
    } else if (data.payment_plan_id && !data.is_deposit) {
      price = data.item_price * data.payment_plan_id;
    } else {
      // Use remaining_amount if it exists, otherwise fallback to item_price
      price = data.remaining_amount ?? data.item_price;
    }

    total += price * +data.quantity;
    return data;
  });
  return total;
};
const getLineItemBasePrice = (item) => {
  if (item.payment_plan_id && item.is_deposit) {
    return (
      Number(item.remaining_amount || 0) * Number(item.payment_plan_id || 0)
    );
  }
  if (item.payment_plan_id && !item.is_deposit) {
    return Number(item.item_price || 0) * Number(item.payment_plan_id || 0);
  }
  return Number(item.remaining_amount ?? item.item_price ?? 0);
};

const isAddressComplete = (address) => {
  return (
    address?.address && address?.zip_code && address?.country && address?.city
  );
};

const TilledPaymentForm = ({
  cartItems,
  discount,
  fee,
  storePromo,
  selectedAddress,
  paymentMethod,
  promoDiscountValue,
  promoDiscountType,
  userData,
  wizardSettings,
}) => {//NOSONAR
  // Separate cartItems based on payment_plan_id
  const [paymentOption, setPaymentOption] = useState(null);
  const [selectedSavedCardId, setSelectedSavedCardId] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [saveCardForFuture, setSaveCardForFuture] = useState(false);
  const [detectedCardBrand, setDetectedCardBrand] = useState('');
  const teamLabel = wizardSettings?.team_label || 'Team';
  const { user } = useAuthUser();
  const updatedCartItems = cartItems?.map((item) => ({
    ...item,
    user_id: item.user_id ?? user?.id,
  }));
  const fullPayments = updatedCartItems?.filter(
    (item) => item?.payment_plan_id === null && item?.is_deposit === 0,
  );

  const recurringPayments = updatedCartItems?.filter(
    (item) => item?.payment_plan_id !== null && item?.is_deposit === 0,
  );
  const depositPayments = updatedCartItems?.filter(
    (item) => item?.is_deposit === 1,
  );

  const [tilled, setTilled] = useState(null);
  const tilledFormRef = useRef(null);
  const navigate = useNavigate();
  const companyTheme = CompanyTheme();
  const totalPriceForFullPayemnt = getTotalPrice(fullPayments);
  const totalPriceForRecurringPayment = getTotalPrice(recurringPayments);
  const totalPriceForDepositePayments = getTotalPrice(depositPayments);
  const totalPrice = getTotalPrice(cartItems);
  const discountValue = discount ? (promoDiscountValue / totalPrice) * 100 : 0;
  const { tournamentSlug } = useParams();
  let fullPaymentDiscount = 0;

  if (discount) {
    if (promoDiscountType === 'percentage') {
      fullPaymentDiscount =
        (totalPriceForFullPayemnt * parseFloat(promoDiscountValue)) / 100;
    } else if (promoDiscountType === 'fixed') {
      fullPaymentDiscount = (
        (totalPriceForFullPayemnt * parseFloat(discountValue)) /
        100
      ).toFixed(2);
    }
  }

  const discountedTotalForFullPayment =
    discount && promoDiscountType
      ? totalPriceForFullPayemnt - fullPaymentDiscount
      : totalPriceForFullPayemnt;

  const feePercentageForFullPayment = fee
    ? discountedTotalForFullPayment * (fee / 100)
    : 0;

  const discountedTotalForRecurringPayment = discount
    ? totalPriceForRecurringPayment - discount
    : totalPriceForRecurringPayment;

  const feePercentageForRecurringPayment = fee
    ? discountedTotalForRecurringPayment * (fee / 100)
    : 0;

  const totalWithFeeForFullPayment = (
    discountedTotalForFullPayment + feePercentageForFullPayment
  ).toFixed(2);

  const totalWithFeeForRecurringPayment = (
    discountedTotalForRecurringPayment + feePercentageForRecurringPayment
  ).toFixed(2);

  const [loader, setLoader] = useState(false);
  const infoViewActionsContext = useInfoViewActionsContext();
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const checkFormValidity = (
    cardNumberField,
    cardExpiryField,
    cardCvvField,
  ) => {
    const isValid =
      cardNumberField._valid && cardExpiryField._valid && cardCvvField._valid;
    setIsFormValid(isValid);
  };

  const getTilledCredentials = (paymentMethodData) => {
    if (!paymentMethodData) return {};
    return paymentMethodData.payment_mode === 'live'
      ? {
          publicKey: paymentMethodData.live_public_key,
          accountId: paymentMethodData.live_merchant_id,
          isLive: true,
        }
      : {
          publicKey: paymentMethodData.test_public_key,
          accountId: paymentMethodData.test_merchant_id,
          isLive: false,
        };
  };

  const initializeCardFields = async (tilledForm) => {
    const fieldOptions = {
      styles: {
        base: {
          fontFamily: 'Helvetica Neue, Arial, sans-serif',
          color: '#304166',
          fontWeight: '400',
          fontSize: '14px',
          '::placeholder': { color: '#94A3B8' },
        },
        invalid: { ':hover': { textDecoration: 'underline dotted red' } },
        valid: { color: '#00BDA5' },
      },
    };

    const cardNumberField = tilledForm
      .createField('cardNumber', {
        ...fieldOptions,
        placeholder: 'Card Number',
      })
      .inject('#card-number-container');

    const cardExpiryField = tilledForm
      .createField('cardExpiry', {
        ...fieldOptions,
        placeholder: 'MM / YY',
      })
      .inject('#card-expiration-container');

    const cardCvvField = tilledForm
      .createField('cardCvv', { ...fieldOptions, placeholder: 'CVV' })
      .inject('#card-cvv-container');

    // Validate form initially
    checkFormValidity(cardNumberField, cardExpiryField, cardCvvField);

    // Attach change event listeners
    [cardNumberField, cardExpiryField, cardCvvField].forEach((field) =>
      field.on('change', () =>
        checkFormValidity(cardNumberField, cardExpiryField, cardCvvField),
      ),
    );
    cardNumberField.on('change', handleCardBrandChange);

    return tilledForm.build();
  };
  useEffect(() => {
    if (user?.id) {
      fetchSavedCards();
    } else {
      setPaymentOption('new');
    }
  }, []);

  const fetchSavedCards = async () => {
    try {
      setIsLoading(true);
      const res = await jwtAxios.get(`/subscriptionsPayments/wallet/cards/${user.id}`);
      const list = res.data?.list || [];
      setSavedCards(list);
      if (list.length > 0) {
        setPaymentOption('saved');
        const defaultCard = selectedSavedCardId
          ? list.find((c) => c.id === selectedSavedCardId)
          : list.find((c) => c.is_default) || list[0];
        setSelectedSavedCardId(defaultCard.id);
        setDetectedCardBrand(normalizeCardBrandKey(defaultCard?.brand));
      } else {
        setPaymentOption('new');
        setSelectedSavedCardId(null);
        setDetectedCardBrand('');
      }
      setIsLoading(false);
    } catch (err) {
      setSavedCards([]);
      setPaymentOption('new');
      setDetectedCardBrand('');
      setIsLoading(false);
    }
  };
  const handleCardBrandChange = (evt) => {
    const cardBrand = evt.brand;
    setDetectedCardBrand(normalizeCardBrandKey(cardBrand));
  };

  const clearCardContainers = () => {
    [
      '#card-number-container',
      '#card-expiration-container',
      '#card-cvv-container',
    ].forEach((id) => {
      const container = document.querySelector(id);
      if (container) container.innerHTML = '';
    });
  };

  useEffect(() => {
    // Only initialize Tilled when the "new card" UI is actually mounted.
    // The #card-*-container divs are conditionally rendered under
    // `paymentOption === 'new'`, so initializing any earlier races the DOM
    // and, combined with the dep-array re-run, leaks duplicate iframes.
    if (!paymentMethod) return undefined;
    if (paymentOption != 'new') return undefined;

    let cancelled = false;

    const initializeTilled = async () => {
      try {
        const { publicKey, accountId, isLive } =
          getTilledCredentials(paymentMethod);
        const tilledInstance = new window.Tilled(publicKey, accountId, {
          sandbox: !isLive,
        });
        if (cancelled) return;
        setTilled(tilledInstance);

        const form = await tilledInstance.form({
          payment_method_type: 'card',
        });
        if (cancelled) {
          try {
            form.teardown();
          } catch (e) {
            /* noop */
          }
          return;
        }

        // Make sure containers are clean before injecting, so we never stack
        // two sets of Tilled iframes under StrictMode / remounts.
        clearCardContainers();

        await initializeCardFields(form);
        if (cancelled) {
          try {
            form.teardown();
          } catch (e) {
            /* noop */
          }
          clearCardContainers();
          return;
        }

        tilledFormRef.current = form;
      } catch (error) {
        console.error('Failed to load Tilled script:', error);
      }
    };

    initializeTilled();

    return () => {
      cancelled = true;
      setTilled(null);
      if (tilledFormRef.current) {
        try {
          tilledFormRef.current.teardown();
        } catch (e) {
          /* noop */
        }
        tilledFormRef.current = null;
      }
      clearCardContainers();
    };
  }, [paymentMethod, paymentOption]);

  useEffect(() => {
    if (paymentOption == 'saved') {
      const selectedCard = savedCards.find((c) => c.id === selectedSavedCardId);
      setDetectedCardBrand(normalizeCardBrandKey(selectedCard?.brand));
      return;
    }
    if (paymentOption == 'new') {
      setDetectedCardBrand('');
    }
  }, [paymentOption, selectedSavedCardId, savedCards]);

  const handlePayment = async () => {
    try {
      let res;
      setLoader(true);
      // Final submission recheck to handle concurrent registrations.
      await jwtAxios.post('/tournament-insecure/validate-division-capacity', {
        cartItems: updatedCartItems,
      });

      if (fullPayments.length > 0) {
        res = await processFullPayment();
      }

      if (recurringPayments.length > 0) {
        await processRecurringPayment();
      }

      if (depositPayments.length > 0) {
        await processDepositPayment();
      }
      if (res.status === 200) {
        setLoader(false);
        navigate(`/order/confirmation/${res.id}`);
        // ✅ Only runs if ALL above succeed
        clearProgress(tournamentSlug, getSaveProgressUserId(user));
      }
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handlePaymentError(error);
    }
  };
  const processFullPayment = async () => {
    try {
      const paymentMethodId =
        paymentOption == 'saved' && selectedSavedCardId
          ? selectedSavedCardId
          : await createPaymentMethod();
      const fullPaymentsData = fullPayments.map((d) => ({
        division_id: d.division_id,
        team_name: d.team_name,
        coach_name: d.coach_name,
        user_id: d.user_id,
        team_id: d.team_id,
        tournament_id: d.tournament_id,
        id: d.id,
        tournament_name: d.tournament_name,
      }));
      const response = await createPaymentIntent(
        '/tournament-insecure/tilled/create-payment-intent',
        fullPaymentsData,
        totalWithFeeForFullPayment,
        fullPaymentDiscount,
        await paymentTax(
          totalWithFeeForFullPayment,
          feePercentageForFullPayment.toFixed(2),
        ),
        paymentMethodId,
        undefined,
        saveCardForFuture,
      );
      return { status: response.status, id: response.data.id };
    } catch (error) {
      const err =
        error?.response?.data?.error || error?.response?.data?.message || error;
      handlePaymentError(err);
      return { status: 500 };
    }
  };

  const processRecurringPayment = async () => {
    try {
      const paymentMethodId = await createPaymentMethod();
      const response = await createPaymentIntent(
        '/subscriptionsPayments',
        recurringPayments,
        totalWithFeeForRecurringPayment,
        discountValue,
        await paymentTax(totalWithFeeForRecurringPayment),
        paymentMethodId,
        fullPayments.length <= 0,
        saveCardForFuture,
      );

      if (depositPayments.length === 0) {
        navigate(`/order/confirmation/${response.data.subscriptionsStatus.id}`);
      }
    } catch (error) {
      handlePaymentError(error);
    }
  };

  const processDepositPayment = async () => {
    try {
      const paymentMethodId = await createPaymentMethod();
      const response = await createPaymentIntent(
        '/subscriptionsPayments/deposit/payments',
        depositPayments,
        totalPriceForDepositePayments,
        discount === '' ? null : discountValue,
        await paymentTax(totalWithFeeForRecurringPayment),
        paymentMethodId,
        fullPayments.length <= 0 && recurringPayments.length <= 0,
        saveCardForFuture,
      );
      navigate(`/order/confirmation/${response.data.transId}`);
    } catch (error) {
      handlePaymentError(error);
    }
  };

  const createPaymentIntent = async (
    url,
    cartItem,
    totalAmount,
    coupon,
    tax,
    paymentMethodId,
    applyPromo,
    saveCard,
  ) => {
    return await jwtAxios.post(url, {
      cartItems: cartItem,
      total_amount: totalAmount,
      user_id: user?.id || userData.user_id,
      coupon: discount === '' ? null : coupon,
      tax,
      storePromo,
      address: formatAddress(selectedAddress),
      paymentMethodId,
      applyPromo,
      ...(url === '/tournament-insecure/tilled/create-payment-intent' && {
        saveCard: !!saveCard,
      }),
    });
  };

  const paymentTax = async (totalWithFeeForPayment, feePercentagePayment) => {
    let PaymentTax = null;

    if (fee !== '') {
      PaymentTax =
        totalWithFeeForPayment === 0 ? 0 : feePercentagePayment || fee;
    }
    return PaymentTax;
  };

  const createPaymentMethod = async () => {
    const paymentMethodResponse = await tilled?.createPaymentMethod({
      type: 'card',
      billing_details: formatBillingDetails(selectedAddress, user || userData),
    });

    if (paymentMethodResponse.error) {
      throw new Error(paymentMethodResponse.error.message);
    }

    return paymentMethodResponse.id;
  };

  const formatAddress = (address) => ({
    address: address?.address,
    city: address?.city,
    state: address?.state,
    zipcode: address?.zip_code,
    country: address?.country,
  });

  const formatBillingDetails = (address, userDetails) => ({
    name: `${userDetails.first_name} ${userDetails.last_name}`,
    address: {
      street: address?.address,
      city: address?.city,
      state: address?.state,
      zip: address?.zip_code?.toString(),
      country: address?.country,
    },
    email: userDetails.email,
    phone: userDetails.user_mobile,
  });

  const handlePaymentError = (error) => {
    console.error('Payment Error:', error);
    infoViewActionsContext.fetchError(
      error?.response?.data?.error || error?.response?.data?.message || error,
    );
  };
  const canSubmit = canFormSubmit(
    loader,
    isAddressComplete,
    selectedAddress,
    paymentOption,
    selectedSavedCardId,
    isFormValid,
  );
  const cardChipConfig = [
    { label: 'VISA', key: 'visa', color: '#1a1f71' },
    { label: 'MC', key: 'mastercard', color: '#eb001b' },
    { label: 'AMEX', key: 'amex', color: '#2e77bc' },
    { label: 'DISC', key: 'discover', color: '#ff6000' },
  ];
  const summaryName =
    fullPayments?.[0]?.tournament_name ||
    updatedCartItems?.[0]?.tournament_name ||
    'Tournament Registration';
  const subtotalValue = Number(totalPriceForFullPayemnt || totalPrice || 0);
  const discountAmount = Number(fullPaymentDiscount || 0);
  const convenienceFeeAmount = Number(feePercentageForFullPayment || 0);
  const grandTotal = Number(
    totalWithFeeForFullPayment || subtotalValue + convenienceFeeAmount,
  );
  const summaryItems = (
    fullPayments?.length > 0 ? fullPayments : updatedCartItems
  )
    .map((item) => {
      const qty = Number(item?.quantity || 1);
      const unitPrice = getLineItemBasePrice(item);
      return {
        id: item?.id || `${item?.team_id || ''}-${item?.division_id || ''}`,
        teamName: item?.team_name || 'Team',
        tournamentName: item?.tournament_name || 'Tournament',
        divisionName: item?.division_name || 'Division',
        quantity: qty,
        unitPrice,
        lineTotal: unitPrice * qty,
      };
    })
    .filter((item) => Number.isFinite(item.lineTotal));
  return (
    <Card
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3 },
        maxWidth: 650,
        mx: 'auto',
        borderRadius: 3,
        boxShadow: 2,
      }}
    >
      <CardContent
        sx={{
          p: { xs: 2, sm: 3 },
          '&:last-child': { pb: { xs: 2, sm: 3 } },
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Typography variant='h4' fontWeight='bold'>
          Payment
        </Typography>
        <Typography sx={{ mb: 2, fontSize: 18 }}>
          All transactions are encrypted and secure
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'center' },
            gap: 1,
            mb: 1.5,
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Typography
            sx={{ fontSize: 15, fontWeight: 600, color: 'text.secondary' }}
          >
            Accepted
          </Typography>
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 600,
              color: 'text.secondary',
              display: 'flex',
              gap: 2,
            }}
          >
            {cardChipConfig.map((brand) => {
              const isActive = detectedCardBrand === brand.key;
              return (
                <Chip
                  key={brand.key}
                  label={brand.label}
                  size='small'
                  sx={{
                    height: 32,
                    borderRadius: 1,
                    border: isActive
                      ? `2px solid ${brand.color}`
                      : '1px solid #c8ced8',
                    backgroundColor: isActive
                      ? alpha(brand.color, 0.1)
                      : '#fff',
                    color: brand.color,
                    px: 0.25,
                    boxShadow: isActive
                      ? `0 0 0 1px ${alpha(brand.color, 0.2)}`
                      : 'none',
                    '& .MuiChip-label': {
                      px: 1,
                      fontSize: 13,
                      letterSpacing: 0.4,
                    },
                    fontWeight: 600,
                  }}
                />
              );
            })}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {!isLoading ? (
          <Box
            sx={{
              fontSize: { xs: 10, sm: 12 },
              fontWeight: { xs: Fonts?.LIGHT, sm: Fonts?.MEDIUM },
            }}
          >
            {savedCards.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2.25 }}>
                <RadioGroup
                  row
                  value={paymentOption}
                  onChange={(e) => {
                    setPaymentOption(e.target.value);
                    if (e.target.value === 'new') {
                      setPaymentOption('new');
                      setSelectedSavedCardId(null);
                      setSaveCardForFuture(false);
                      setDetectedCardBrand('');
                    } else {
                      setPaymentOption('saved');
                      const card = savedCards[0];
                      setSelectedSavedCardId(card.id);
                      setSaveCardForFuture(false);
                      setDetectedCardBrand(normalizeCardBrandKey(card?.brand));
                    }
                  }}
                >
                  <FormControlLabel
                    value='saved'
                    control={<Radio />}
                    label={
                      <Typography sx={{ fontSize: { xs: 12, sm: 14 }, pb: 0 }}>
                        Use Existing Card
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value='new'
                    control={<Radio />}
                    label={
                      <Typography sx={{ fontSize: { xs: 12, sm: 14 }, pb: 0 }}>
                        Use New Card
                      </Typography>
                    }
                  />
                </RadioGroup>
              </FormControl>
            )}

            {paymentOption == 'saved' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id='payment-method-label'>Select Card</InputLabel>
                <Select
                  labelId='payment-method-label'
                  id='payment-method-select'
                  value={
                    paymentOption == 'saved' && selectedSavedCardId
                      ? selectedSavedCardId
                      : 'new'
                  }
                  label='Select Card'
                  onChange={(e) => {
                    const val = e?.target.value;
                    if (val === 'new') {
                      setPaymentOption('new');
                      setSelectedSavedCardId(null);
                    } else {
                      setPaymentOption('saved');
                      setSelectedSavedCardId(val);
                    }
                  }}
                >
                  {savedCards.map((card) => (
                    <MenuItem key={card.id} value={card.id}>
                      {brandLabel(card.brand)} •••• {card.last4}
                      {card.is_default && (
                        <Typography
                          component='span'
                          sx={{
                            ml: 1,
                            fontSize: '0.75rem',
                            color: getColor(companyTheme),
                            fontWeight: 600,
                          }}
                        >
                          (Default)
                        </Typography>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {paymentOption == 'saved' &&
              selectedSavedCardId &&
              savedCards.length > 0 && (
                <Box
                  sx={{
                    mb: 2,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 1.5,
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <Typography variant='body2' color='text.secondary'>
                    Using saved card:{' '}
                    {brandLabel(
                      savedCards.find((c) => c.id === selectedSavedCardId)
                        ?.brand,
                    )}{' '}
                    ••••{' '}
                    {
                      savedCards.find((c) => c.id === selectedSavedCardId)
                        ?.last4
                    }
                    {savedCards.find((c) => c.id === selectedSavedCardId)
                      ?.is_default && (
                      <Typography
                        component='span'
                        sx={{
                          ml: 0.5,
                          fontSize: '0.75rem',
                          color: getColor(companyTheme),
                          fontWeight: 600,
                        }}
                      >
                        (Default)
                      </Typography>
                    )}
                    {savedCards.find((c) => c.id === selectedSavedCardId)
                      ?.billing_address && (
                      <>
                        <br />
                        {
                          savedCards.find((c) => c.id === selectedSavedCardId)
                            ?.billing_address
                        }
                      </>
                    )}
                  </Typography>
                </Box>
              )}

            {paymentOption == 'new' && (
              <>
                <Typography sx={{ mb: 0.5, fontWeight: 500 }}>
                  Card number
                </Typography>
                <Box sx={{ position: 'relative', mb: 2.5 }}>
                  <div
                    id='card-number-container'
                    placeholder='xxxx xxxx xxxx xxxx'
                    style={{
                      padding: '12px',
                      width: '100%',
                      border: '1px solid #ccc',
                      borderRadius: '10px',
                      boxSizing: 'border-box',
                      margin: 0,
                      height: '46px',
                      background: 'white',
                      fontSize: '14px',
                      fontFamily: 'helvetica, tahoma, calibri, sans-serif',
                    }}
                  ></div>
                  <i
                    id='credit-card-logo'
                    className='fas fa-credit-card'
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '24px',
                      color: '#304166',
                    }}
                  ></i>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 1.5,
                  }}
                >
                  <Box>
                    <Typography sx={{ mb: 0.5, fontWeight: 500 }}>
                      Expiry date
                    </Typography>
                    <div
                      id='card-expiration-container'
                      style={{
                        padding: '12px',
                        width: '100%',
                        border: '1px solid #ccc',
                        borderRadius: '10px',
                        boxSizing: 'border-box',
                        margin: '0 0 12px',
                        height: '46px',
                        background: 'white',
                        fontSize: '14px',
                        fontFamily: 'helvetica, tahoma, calibri, sans-serif',
                      }}
                    ></div>
                  </Box>
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography sx={{ fontWeight: 500 }}>CVV</Typography>
                      <Typography sx={{ fontSize: 12 }}>3-4 digits</Typography>
                    </Box>
                    <div
                      id='card-cvv-container'
                      placeholder='CVV'
                      style={{
                        padding: '12px',
                        width: '100%',
                        border: '1px solid #ccc',
                        borderRadius: '10px',
                        boxSizing: 'border-box',
                        margin: '0 0 12px',
                        height: '46px',
                        background: 'white',
                        fontSize: '14px',
                        fontFamily: 'helvetica, tahoma, calibri, sans-serif',
                      }}
                    ></div>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mt: 1,
                    color: '#6b7280',
                    fontSize: 13,
                  }}
                >
                  <InfoOutlinedIcon fontSize='small' sx={{ mr: 1 }} />
                  Please fill the above fields manually. Please submit payment
                  only after fields turn green.
                </Box>
                <Grid className='tournament' size={{ xs: 12, md: 12 }}>
                  <Box className='new-card-box'>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={saveCardForFuture}
                          onChange={(event) =>
                            setSaveCardForFuture(event.target.checked)
                          }
                        />
                      }
                      label='Save card for future purchases'
                    />
                  </Box>
                </Grid>
              </>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mt: 1,
                py: 1,
                color: 'text.secondary',
              }}
            >
              <SecurityOutlinedIcon
                fontSize='small'
                sx={{ mr: 1, color: 'green' }}
              />
              Secured by Tilled · Your card details are never stored on our
              servers
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'grid', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>{summaryName}</Typography>
                <Typography>${subtotalValue.toFixed(2)}</Typography>
              </Box>
              {discountAmount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Discount</Typography>
                  <Typography>-${discountAmount.toFixed(2)}</Typography>
                </Box>
              )}
              {convenienceFeeAmount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>Convenience fee ({fee || 0}%)</Typography>
                  <Typography>${convenienceFeeAmount.toFixed(2)}</Typography>
                </Box>
              )}
              <Divider sx={{ my: 1.25 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontWeight: 700 }}>
                  Total due today
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  ${grandTotal.toFixed(2)}
                </Typography>
              </Box>
            </Box>
            {summaryItems.length > 0 && (
              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  mt: 1.5,
                  border: '1px solid #e5e7eb',
                  borderRadius: '10px !important',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 42,
                    '& .MuiAccordionSummary-content': { my: 1 },
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 600, pb: 0 }}>
                    View {teamLabel} breakdown ({summaryItems.length} item
                    {summaryItems.length > 1 ? 's' : ''})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0.5, pb: 1.25 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 1,
                      py: 0.75,
                      borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                    }}
                  >
                    {summaryItems.map((item) => {
                      let teamDetail = [
                        item?.teamName || 'Unnamed Team',
                        item?.divisionName,
                        item.tournamentName,
                      ]
                        .filter(Boolean)
                        .join(' - ');
                      return (
                        <Box
                          key={item.id}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1,
                            py: 0.75,
                            borderBottom: '1px solid #f1f5f9',
                          }}
                        >
                          <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 300 }}>
                              {teamDetail}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: 12, fontWeight: 300 }}>
                            ${item.lineTotal.toFixed(2)}
                          </Typography>
                        </Box>
                      );
                    })}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        pt: 0.5,
                      }}
                    >
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                        Items subtotal
                      </Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                        $
                        {summaryItems
                          .reduce((sum, item) => sum + item.lineTotal, 0)
                          .toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            <Button
              id='submit-btn'
              sx={{
                position: 'relative',
                mt: 3,
                py: 1.2,
                backgroundColor: getColor(companyTheme),
                '&:hover': {
                  backgroundColor: getbColor(companyTheme),
                },
              }}
              variant='contained'
              fullWidth
              onClick={handlePayment}
              disabled={!canSubmit || loader}
            >
              {loader ? (
                <CircularProgress size={22} color='inherit' />
              ) : (
                `Pay $${grandTotal.toFixed(2)}`
              )}
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              minHeight: 220,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <AppInfoView />

        {loader && <AppLoader />}
      </CardContent>
    </Card>
  );
};

TilledPaymentForm.propTypes = {
  cartItems: PropTypes.array, // Array of items in the cart
  discount: PropTypes.number, // Discount amount
  fee: PropTypes.number, // Additional fees
  storePromo: PropTypes.bool, // Whether store promo is applied
  selectedAddress: PropTypes.object, // Selected address object
  paymentMethod: PropTypes.string, // Payment method selected
  promoDiscountValue: PropTypes.number, // Promo discount value
  promoDiscountType: PropTypes.string, // Type of promo discount (e.g., percentage, fixed)
  userData: PropTypes.object,
  wizardSettings: PropTypes.object,
};
export default TilledPaymentForm;
